<template>
  <MetaInfo :title="meta.title" :description="meta.description"></MetaInfo>

  <div>
    <navigation></navigation>

    <PageHeader title="AI Terms"> </PageHeader>

    <div class="container-fluid">
      <div class="row">
        <div class="col-xs-12 col-md-3 mb-20">
          <TermsNav></TermsNav>
        </div>
        <div class="col-xs-12 col-md-9">
          <div class="longform">
            <h3>1. Applicability</h3>
            <p>
              1.1. These AI terms, <strong>("AI Terms")</strong> apply to the provision of all Services provided 
              by the Company to the Customer that incorporate AI Functionality. The provisions of these AI 
              Terms are incorporated into and form part of the Agreement. 
            </p>
            <p>
              1.2. All terms in capitals used in these AI Terms, shall have the meaning set out in the 
              Agreement unless defined otherwise in these AI Terms.
            </p>
            
            <h3>2. Definitions</h3>
            <div class="terms-table terms-table--2-col">
              <table>
                <tbody>
                  <tr>
                    <th valign="top">"Agreement"</th>
                    <td valign="top">
                      means the agreement between the parties for the provision of the Services by the Company;
                    </td>
                  </tr>
                  <tr>
                    <th valign="top">"AI Functionality"</th>
                    <td valign="top">
                      means the artificial intelligence-powered features and capabilities integrated 
                      into the AI Products that use data analysis, pattern recognition, natural 
                      language processing, prediction and automated decision support to process 
                      Input to generate Output using AI models or systems of both the Company and 
                      AI Providers, as defined more specifically below for each AI Product;
                      <br><br>
                      <strong>"AI Content Assist"</strong>
                      Natural language draft e-mail content is generated from Input provided by Users.
                      <br><br>

                      <strong>"AI Brand Analyser"</strong>
                      Brand assets, company details and visual styling information are extracted from a 
                      website URL provided by the User to create a reusable brand profile within the 
                      Company's platform.
                      <br/><br/>
                      The AI Brand Analyser uses OpenAI's model to interpret publicly available content 
                      from the supplied website and identify key brand elements such as logo, colours, 
                      fonts, business type, business overview  and imagery.
                      <br><br>

                      <strong>"Account Monitoring"</strong>
                      AI is used to assist with ensuring accounts meet the Company’s terms of service.
                      <br><br>

                      <strong>"Campaign Suggestions"</strong>
                      Campaign suggestions uses OpenAI to assist in identifying areas in which users 
                      might be able to improve their campaign performance.
                      <br/><br/>
                      Campaign suggestions uses OpenAI's language model to interpret and create suggestions
                       based on the users campaign content and performance statistics.
                      <br><br>
                      <strong>"Conversations Sentiment"</strong>
                      Conversations can be analysed by OpenAI, to allow the user to make decisions 
                      and automate based on the sentiment detected.
                      <br/><br/>
                      Conversations Sentiment uses OpenAI's model to produce a sentiment score.
                      <br><br>
                      <strong>"Web Chat AI Assistant"</strong>
                      A natural-language chat response is generated from Input provided by Users or website 
                      visitors via Capsule’s Conversations feature.
                      <br/><br/>
                      The AI Assistant uses OpenAI's language model to interpret and draft helpful replies,
                      suggestions, or next-step prompts in real time based on content provided
                      by the customer.
                      <br><br>

                      <strong>"Transpond MCP"</strong>
                      A hosted server that exposes Transpond tools (read and write operations on
                      Customer Data) via the Model Context Protocol, allowing an AI client selected
                      and operated by the Customer to query, and where enabled, create, update or
                      delete Customer Data in response to natural language instructions from Users.
                    </td>
                  </tr>
                  <tr>
                    <th valign="top">"AI Product(s)"</th>
                    <td valign="top">
                      means any Services ordered by the Customer that include AI Functionality;
                    </td>
                  </tr>
                  <tr>
                    <th valign="top">"AI Providers"</th>
                    <td valign="top">
                      means the third party providers of AI Functionality listed in Appendix 1 of these AI Terms;
                    </td>
                  </tr>
                  <tr>
                    <th valign="top">"Company"</th>
                    <td valign="top">
                      means the supplier of the Services;
                    </td>
                  </tr>
                  <tr>
                    <th valign="top">"Content"</th>
                    <td valign="top">
                      means the Input and Output together;
                    </td>
                  </tr>
                  <tr>
                    <th valign="top">"Customer"</th>
                    <td valign="top">
                      means the customer who owns the account from which the Services are accessed;
                    </td>
                  </tr>
                  <tr>
                    <th valign="top">"Input"</th>
                    <td valign="top">
                      means all data provided by the Customer and/or Users which is used by the AI Functionality 
                      to produce Output, as defined more specifically below for each AI Product:
                      <br><br>
                      <strong>"AI Content Assist"</strong>
                      Users type information into an prompt which includes but is not limited to personal 
                      or confidential business data (e.g. contact names, job titles, company details, or 
                      message context). No input is provided by the Company;
                      <br><br>

                      <strong>"AI Brand Analyser"</strong>
                      Users provide their own website URL or a URL they control for the purpose of importing
                       brand assets into Transpond.
                      <br/><br/>
                      The AI model may access and analyse publicly available content from that website, 
                      including logos, colour palettes, business information, font styles, and images.
                      <br/><br/>
                      This Input may contain limited personal or business information that is publicly 
                      visible on the User's site (e.g. contact details or team information).
                      <br/><br/>
                      No Input is provided by the Company, and no third-party enrichment or web 
                      crawling occurs beyond the submitted URL.
                      <br><br>

                      <strong>"Account Monitoring"</strong>
                      Users provide content when creating an account, requesting approval for an 
                      account and creating templates.
                      <br/><br/>
                      The input may contain personal data or confidential business information 
                      (e.g. names, email addresses, company details)
                      <br/><br/>
                      No input is provided by the Company.
                      <br/><br/>
                      The data is transmitted securely to OpenAI for response generation.
                      <br/><br/>

                      <strong>"Campaign Suggestions"</strong>
                      Input consists of the user’s industry, country, campaign statistics 
                      (e.g open rate, click rate, bounce rate, unsubscribe rate), 
                      subject lines, email content.
                      <br/><br/>
                      The input may contain personal data or confidential business information 
                      (e.g names, email addresses, company details)
                      <br/><br/>
                      The data is transmitted securely to OpenAI for response generation.
                      <br/><br/>

                      <strong>"Conversations Sentiment"</strong>
                      Input consists of inbound chat or email messages entered by Users and their 
                      customers through the Conversations Webchat interface, email, or Facebook Messenger.
                      <br/><br/>
                      This Input may include personal data or confidential business information 
                      (e.g. names, email addresses, company details, or enquiry content).
                      <br/><br/>
                      No Input is provided by the Company.
                      <br/><br/>
                      The data is transmitted securely to OpenAI for response generation.
                      <br/><br/>

                      <strong>"Web Chat AI Assistant"</strong>
                      Input consists of the chat messages and context entered by  Users and their 
                      customer through the Conversations Conversations Webchat interface, email, 
                      or Facebook Messenger.
                      <br/><br/>
                      This Input may include personal data or confidential business information 
                      (e.g. names, email addresses, company details, or enquiry content).
                      <br/><br/>
                      No Input is provided by the Company.
                      <br/><br/>
                      The data is transmitted securely to OpenAI for response generation.
                      <br/><br/>

                      <strong>"Transpond MCP"</strong>
                      Instructions given by Users to their chosen AI client, and the Customer Data
                      retrieved from the Service in response to tool calls made by that AI client
                      via Transpond MCP. This Input may include personal data and confidential
                      business information. No Input is provided by the Company. You are
                      responsible for any text, images, or other content you upload or submit.
                    </td>
                  </tr>
                  <tr>
                    <th valign="top">"Output"</th>
                    <td valign="top">
                      means all content or results generated by each AI Product, based on the Input, 
                      as defined more specifically below for each AI Product:
                      <br><br>
                      <strong>"AI Content Assist"</strong>
                      Natural-language content is generated from the input data supplied by the User. 
                      The Company does not add, modify, or combine any information in the output.
                      <br/><br/>
                      The response is moderated through OpenAI's moderation endpoint before being 
                      returned to the User;
                      <br/><br/>

                      <strong>"AI Brand Analyser"</strong>
                      A structured brand profile is generated from the analysed website data, 
                      including extracted colour values, fonts, logo files, business type, 
                      business overview, and example imagery.
                      <br/><br/>
                      The Output is provided solely to the User's Transpond account for reuse in templates, 
                      campaigns, and other design tools.
                      <br/><br/>
                      The Company does not add, modify, or combine any information in the Output.
                      <br/><br/>
                      The response is processed securely through OpenAI's API and returned to the Company.
                      <br/><br/>

                      <strong>"Account Monitoring"</strong>
                      Output is stored against the User's account and may be used to assist the Company 
                      with quality control and account approval decisions.
                      <br/><br/>
                      The response is processed securely through OpenAI's API and return to the Company.
                      <br/><br/>

                      <strong>"Campaign Suggestions"</strong>
                      A structured suggestions response is generated from the analysed campaign data, 
                      including textual suggestions on how a user can improve their performance.
                      <br/><br/>
                      The company does not add, modify or combine and information in the output.
                      <br/><br/>
                      The response is processed securely though OpenAI's API an returned to the Company.
                      <br/><br/>

                      <strong>"Conversations Sentiment"</strong>
                      A sentiment score is generated from the Input data.
                      <br/><br/>
                      The Company does not add, modify, or combine any information in the Output.
                      The response is processed through OpenAI's API and returned securely to the User 
                      or displayed in the chat interface.
                      <br/><br/>
                      
                      <strong>"Web Chat AI Assistant"</strong>
                      A natural-language draft chat message or suggestion is generated from the Input data.
                      <br/><br/>
                      The Output may reflect personal or confidential information contained 
                      in the Input but is produced solely from that context.
                      <br/><br/>
                      The Company does not add, modify, or combine any information in the Output.
                      <br/><br/>
                      The response is processed through OpenAI's API and returned securely to the User or displayed
                      in the chat interface.
                      <br/><br/>

                      <strong>"Transpond MCP"</strong>
                      Data returned from the Service via Transpond MCP tool calls, and any changes
                      made to Customer Data as a direct result of tool calls executed by the
                      Customer's chosen AI client. The Company does not generate, moderate, or
                      otherwise control the natural language responses produced by the Customer's
                      chosen AI client. You are responsible for any text, images, or other content
                      generated based on your AI Input.
                    </td>
                  </tr>
                  <tr>
                    <th valign="top">"Services"</th>
                    <td valign="top">
                      means the cloud services of the Company set out in the Agreement provided to the Customer;
                    </td>
                  </tr>
                  <tr>
                    <th valign="top">"Transpond Connectors"</th>
                    <td valign="top">
                      means official integrations built and published by the Company that make
                      Transpond MCP available within a specific third party AI platform's own
                      directory or connector ecosystem (each a "Connector Platform"), as listed in
                      Appendix 1. Transpond Connectors are distinguished from a Customer's own,
                      independently configured connection to Transpond MCP using an AI client of
                      the Customer's choosing (see clause 5.5), in that the Company has itself
                      selected, built for, and agreed separate developer terms with each Connector
                      Platform provider.
                    </td>
                  </tr>
                  <tr>
                    <th valign="top">"User"</th>
                    <td valign="top">
                      means any Customer authorised user or end user of the Customer who has access to or uses 
                      the AI Functionality or AI Product;
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
            
            <h3>3. Conflicts</h3>
            <p>
              The Company provides Services to the Customer that include AI Products subject 
              to the provisions of these AI Terms, which apply in addition to the other terms 
              of the Agreement. In the event of any conflict between the provisions of these AI 
              Terms and the other terms of the Agreement, the provisions of these AI Terms shall 
              prevail in relation to the AI Products, only.
            </p>

            <h3>4. Input</h3>
            <p>
              4.1. The Customer (or its licensors) own Input provided by the Customer or Users, 
              <strong>("Customer Input")</strong>. 
            </p>
            <p>
              4.2. The Customer represents and warrants that it (or its licensors) 
              have obtained all rights, licenses and consents required to permit the 
              Company to use Customer Input within the AI Products using the AI 
              Functionality to create Output. The Customer is solely responsible for 
              ensuring that all Customer Input provided to the Company complies with 
              all applicable laws.
            </p>
            <p>
              4.3. The Customer grants the Company a non-exclusive, worldwide, royalty free, 
              right to permit the Company and its AI Providers to use Customer Input within the 
              AI Products using the AI Functionality for the purpose of generating Output.
            </p>
            <p>
              4.4. The Company only permits AI Providers to access and use Customer Input 
              exclusively to create Output via the AI Functionality for the Customer.
            </p>
            <p>4.5. The Company (or its licensors) own or have the right to use any Input provided 
              by the Company (or its licensors), <strong>("Company Input")</strong>. The Company represents and warrants 
              that it (or its licensors) have obtained all rights, licenses and consents required to 
              permit the Company to use Company Input within the AI Products using the AI Functionality 
              to create Output for the Customer.
            </p>

            <h3>5. AI Providers</h3>
            <p>
              5.1. By using the AI Products, the Customer acknowledges and agrees to the Company using the AI Providers.
            </p>
            <p>
              5.2. Where AI Products are developed using AI models or systems provided by AI Providers, 
              the Customer acknowledges and understands that by using the AI Products the Customer 
              agrees that use of the AI Products is subject to each AI Provider's own terms of use, 
              set out in Appendix 1 of these AI Terms.
            </p>
            <p>
              5.3. The Company only permits AI Providers to access and use Customer Input exclusively 
              to create Output via the AI Functionality for use by the Customer. Customer Input 
              is not used by AI Providers for any other purposes. Customer Input is not used by 
              AI Providers for machine learning of their AI models or systems.
            </p>
            <p>
              5.4. Where any AI Providers process personal data of the Customer or Users, 
              these AI Providers are sub-processors of the Company, who process personal 
              data in providing the AI Products. Any changes to these AI Providers after 
              the date that the Customer is granted access to first start using any AI 
              Products will be notified to the Customer, in accordance with the terms of the DPA.
            </p>
            <p>
              5.5. Where the Customer connects an AI client of its own choosing to Transpond MCP,
              that AI client is not an AI Provider for the purposes of these AI Terms and is not
              listed in Appendix 1. The Company makes no representations as to that AI client's
              behaviour, security, or data handling. The Customer is solely responsible for
              selecting, configuring and evaluating any AI client used with Transpond MCP, and for
              ensuring that client's compliance with the Customer's own data protection obligations.
            </p>
            <p>
              5.6. Notwithstanding clause 5.5, where the Customer accesses Transpond data through a
              Transpond Connector (rather than the Customer's own independently configured
              connection to Transpond MCP), the relevant Connector Platform provider listed in
              Appendix 1 (currently Anthropic, OpenAI, Microsoft, or Google, as applicable) shall be
              treated as an AI Provider for the purposes of clauses 5.1 to 5.4 in respect of that
              Connector only. Use of a Transpond Connector is additionally subject to the relevant
              Connector Platform provider's own developer, directory, and end-user terms, which the
              Company does not control and for which the Company gives no warranty. The Company may
              add, remove, suspend, or modify any Transpond Connector, or its availability on any
              Connector Platform, at any time, including where required by the Connector Platform
              provider's own policies.
            </p>

            <h3>6. Personal Data</h3>
            <p>
              6.1. Where Customer Input includes personal data (or personally identifiable information) 
              of the Customer or Users, as defined by applicable data protection laws, the Customer confirms 
              that it has obtained all relevant consents from Users to permit the Company and the AI 
              Providers to process personal data for the purposes of generating Output in the provision 
              of the AI Products. Such consent includes permission to transfer Customer Input to countries 
              where the AI Providers operate, store, and process the Customer Input.
            </p>
            <p>
              6.2. The Customer warrants that it shall: (i) provide its own privacy policy to Users, 
              setting out the nature of this processing by the Company and the use of the AI Functionality; 
              and (ii) obtain each User's consent to such use of their personal data by the Company; prior 
              to making any User's personal data available for use within the AI Products.
            </p>

            <h3>7. Output</h3>
            <p>7.1. Due to the nature of machine learning, Output may not be unique across all clients 
              and users of the Customer and the Customer acknowledges that the use of AI Functionality 
              within AI Products may generate the same or similar output for other clients and users. 
              Such output created for other clients and users does not form part of the Output. 
            </p>
            <p>
              7.2. The Company authorises the Customer to use Output for their business purposes, as deemed appropriate by the Customer, and in compliance with all applicable laws.
            </p>
            <p>
              7.3. The Company and any AI Provider used to provide the AI Functionality 
              within the AI Products, make no representations or warranties of any kind,
              express or implied, in relation to any Output, including without limitation, 
              the accuracy, availability, suitability, reliability, or content of the Output. 
              The Customer is solely responsible for all Output, in particular ensuring that 
              Output complies with all applicable laws and compliance with the provisions of 
              these AI Terms.
            </p>
            <p>
              7.4. All Output produced is a suggestion designed to assist the Customer and Users 
              in making decisions related to the operation of their business and services. 
              Using the AI Products to create Output does not replace the need for the Customer 
              to use a human to decide upon the suitability of the Output before it is published, 
              used or relied upon by the Customer or Users.
            </p>
            <p>
              7.5. The Customer and Users must check and evaluate the accuracy of any Output 
              before using it and must not rely on the Company or the AI Providers to do this. 
            </p>
            <p>
              7.6. The Customer acknowledges and agrees that the Company has no control over any use 
              of the Output by the Customer or Users. Any use of Output is at the Customer's own risk. 
            </p>
            <p>
              7.7. Prior to publication or distribution of any Output, the Customer must disclose 
              that the Output was generated by AI tools.
            </p>
            <p>
              7.8. The Company has no liability or responsibility: (i) resulting from the use of, 
              or reliance upon any Output by the Customer or Users; or (ii) for any errors or omissions 
              contained in Output or Content.
            </p>
            <p>
              7.9. The AI Products must not be used for contacting emergency services or obtaining 
              immediate emergency assistance. In the event of an emergency, it is crucial to immediately 
              contact the appropriate emergency services in the Customer's jurisdiction or seek direct 
              assistance from local authorities. The Customer acknowledges and agrees that the Company shall 
              not be held liable for any consequences, damages, or losses arising from any inability to use 
              the AI Products for emergency communication or for any reliance placed on the AI Products 
              during emergency situations.
            </p>
            <p>
              7.10. Output is not used by the Company to: (i) train, fine-tune or create derivative models;
              or (ii) retrain or adapt any AI models. Output is generated on demand via an API using stateless
              requests.
            </p>
            <p>
              7.11. Where Transpond MCP tool calls result in the creation, modification, or
              deletion of Customer Data, such changes are made directly and immediately at the
              instruction of the Customer's chosen AI client. The Customer and Users must review
              and approve any tool call before it is executed, particularly where it would change
              or delete Customer Data, and are solely responsible for any resulting changes. The
              Company has no obligation to reverse, and no liability arising from, any such change
              once executed.
            </p>

            <h3>8. Indemnity</h3>
            <p>
              The Customer shall, at its own expense, defend or, at its option, settle any claim or suit 
              relating to Customer Input or Output brought against the Company or the AI Providers by a 
              third party on the basis of infringement of their Intellectual Property Rights or other 
              rights resulting from the Customer Input or Output.
            </p>

            <h3>9. Customer Warranties</h3>
            <p>
              The Customer represents and warrants that it will not, nor will it authorise anyone on its behalf, 
              including a User, to use the AI Products: (i) to mislead anyone that the Output generated by the AI 
              Products is solely human-generated; (ii) to generate content that is prohibited under the terms of 
              the Agreement or any applicable laws; (iii) to generate content that is unlawful, harmful, threatening, 
              defamatory, obscene, harassing or racially or ethnically offensive, facilitates illegal activity, 
              depicts sexually explicit images, or promotes unlawful violence, discrimination based on race, 
              gender, colour, religious belief, sexual orientation, disability, or is otherwise illegal; (iv) 
              to make automated decisions that may have a detrimental impact on individual rights without 
              appropriate human supervision; (v) for activities which have high risk of economic harm, for 
              adult content, adult industries, dating apps, offering tailored financial advice without a 
              qualified person reviewing the information or to provide health advice, political campaigning 
              or lobbying; (vi) in a way that infringes, misappropriates or violates any of the Company's 
              rights or the rights of any third party; (vii) to reverse assemble, reverse compile, decompile, 
              translate or otherwise attempt to discover the source code or underlying components of models, 
              algorithms, and systems of the AI Products (except to the extent such restrictions are contrary 
              to applicable law); (viii) to develop foundation models, other large scale models or AI Products 
              that replicates the AI Products; (ix) to compete with the Company; or (x) to use any method to 
              extract data from the AI Products, including web scraping, web harvesting, or web data extraction 
              methods.
            </p>

            <h3>10. Term and Termination</h3>
            <p>
              The duration of these AI Terms shall commence from the date the AI Products are ordered and continue
              for the term of the Agreement.
            </p>

            <h3>11. No Warranty</h3>
            <p>
              The Company does not guarantee or warrant any features or qualities of the AI Products and all 
              warranties included in the other terms of the Agreement are explicitly excluded in relation to 
              the AI Products. No warranty or undertaking shall be implied by the Customer from any description 
              of the AI Products or any other communication. 
            </p>

            <h3>12. Limitation of Liability</h3>
            <p>
              To the maximum extent permitted by law and in addition to the caps on liability set 
              out in the other terms of the Agreement, the Company shall not be liable to the Customer, 
              Users or any third party for any damages arising in connection with: (i) use of, or 
              inability to use, the AI Products; or (ii) any defect in the AI Products; or (iii) use 
              or reliance upon any Output; or (iv) any third party claims relating to Customer Input. 
              This disclaimer of liability shall apply regardless of the form of action that may be 
              brought against the Company, whether in contract or tort including without limitation 
              any action for negligence.
            </p>

            <h3 style="text-align: center;">Appendix 1 - AI Providers</h3>

            <p>
              The Company currently uses the following AI Providers to provide AI Functionality within the AI Products.
            </p>

            <ul>
              <li>OpenAI: <a href="https://openai.com/policies/terms-of-use/" target="_blank">OpenAI Terms of Service</a></li>
              <li>Anthropic (Claude Connector): <a href="https://www.anthropic.com/legal/consumer-terms" target="_blank">Anthropic Consumer/Commercial Terms of Service and Anthropic Software Directory Terms</a> — AI Provider for Transpond Connectors only, per clause 5.6</li>
            </ul>

          </div>
        </div>
      </div>
    </div>
  </div>
  <site-footer></site-footer>
</template>

<script>
import MetaInfo from "@/components/MetaInfo";
import Navigation from "@/components/navigation/Main";
import SiteFooter from "@/components/SiteFooter";
import TermsNav from "@/components/Terms/TermsNav";
import PageHeader from "@/components/Header/PageHeader";

export default {
  name: "TermsAI",
  components: {
    MetaInfo,
    Navigation,
    SiteFooter,
    TermsNav,
    PageHeader,
  },

  data() {
    return {
      meta: {
        title: "AI Terms - Transpond",
        description: "Take a look at the Transpond AI Terms",
      },
    };
  },
};
</script>
